import { Entity } from "../ecs/entity";
import { Scheduler } from "../ecs/scheduler";
import { Property } from "./property";
/**
 * The simulation container. Owns:
 *   - the time loop (fixed step + accumulator + speed multiplier),
 *   - the entity set,
 *   - the Scheduler that runs registered Systems each fixed step.
 *
 * `step(dtWall)` is called from the render loop (R3F's `useFrame`); the
 * Scheduler runs phases in fixed order. Pausing freezes simulation time;
 * physics may keep running independently if the host wires it that way.
 */
export class World {
    time = new Property(0);
    /** Pauses chemistry / scheduler stepping. Render and grab still run.
     *  Boots `true` (Crocodile-style "set up the bench, then press Play"):
     *  the student arranges reagents, watches indicator colours, then
     *  releases time when ready. Stage's Play / Pause button toggles it. */
    paused = new Property(true);
    speed = new Property(1);
    fixedStep = 1 / 120;
    maxStep = 0.05;
    scheduler = new Scheduler();
    entities = new Map();
    nextId = 1;
    accumulator = 0;
    /** Create a new entity and add it to the world. */
    createEntity() {
        const id = this.nextId++;
        const entity = new Entity(id);
        this.entities.set(id, entity);
        return entity;
    }
    destroyEntity(id) {
        const e = this.entities.get(id);
        if (!e)
            return;
        e.dispose();
        this.entities.delete(id);
    }
    getEntity(id) {
        return this.entities.get(id);
    }
    *allEntities() {
        yield* this.entities.values();
    }
    /** Linear scan over entities matching every component class in `cls`. */
    query(...cls) {
        const out = [];
        outer: for (const e of this.entities.values()) {
            for (const c of cls)
                if (!e.has(c))
                    continue outer;
            out.push(e);
        }
        return out;
    }
    /** Convenience: get the single component of type `cls` from entity `id`. */
    getComponent(id, cls) {
        return this.entities.get(id)?.get(cls);
    }
    /** Advance the simulation. Call from a `useFrame` or RAF callback.
     *  When paused, accumulator is bled to zero so resume starts fresh.
     *
     *  Iterations per frame are capped (`MAX_ITERS_PER_FRAME`) so high
     *  speed multipliers don't choke the renderer — at ×50 the naive
     *  loop would run ~96 fixed steps per 60 Hz frame and the cursor
     *  visibly stuttered on Quest 3 (session 333b). The cap trades some
     *  effective fast-forward rate for a steady framerate; any leftover
     *  accumulator is dropped so it doesn't snowball. */
    static MAX_ITERS_PER_FRAME = 20;
    step(dtWall) {
        if (this.paused.value) {
            this.accumulator = 0;
            return;
        }
        const scaled = Math.min(dtWall, this.maxStep) * this.speed.value;
        this.accumulator += scaled;
        let iters = 0;
        while (this.accumulator >= this.fixedStep
            && iters < World.MAX_ITERS_PER_FRAME) {
            this.scheduler.step(this.fixedStep, this);
            this.time.value = this.time.value + this.fixedStep;
            this.accumulator -= this.fixedStep;
            iters++;
        }
        if (iters >= World.MAX_ITERS_PER_FRAME) {
            this.accumulator = 0;
        }
    }
    reset() {
        for (const e of this.entities.values())
            e.dispose();
        this.entities.clear();
        this.nextId = 1;
        this.accumulator = 0;
        this.time.value = 0;
        this.scheduler.dispose();
    }
}
